import React, { Ref, useMemo } from 'react'
import { AppBar, Box, Toolbar, Hidden, IconButton, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import MenuIcon from '@mui/icons-material/Menu'
import LanguageEditorMenu from './LanguageEditorMenu'
import LanguageSelect from './LanguageSelect'
import { J1LeftarrowC, J2Add, J2Editacontent, J2Userprofile, K1Blindness } from '@e3dc-react/icons'
import TabsBar, { TabsBarTab } from '../Tabs/TabsBar'
import Breadcrumbs, { Breadcrumb } from './Breadcrumbs'
import { PageActions } from '../../State/App/AppModel'
import { useNavigate } from 'react-router-dom'

interface StyleProps {
  alwaysShowTitle?: boolean
  withTabs?: boolean
}

export const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  menuButton: {
    marginLeft: `-${theme.spacing(1)}`,
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'column',
    left: 0,
    right: 0,
    borderRadius: theme.shape.header.borderRadius,
    color: theme.palette.text.header,
    background: theme.palette.background.header,
    alignItems: 'baseline',
    padding: theme.spacing(2),
  },
  headerBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      justifyContent: props.alwaysShowTitle ? 'space-between' : 'flex-end',
    },
  },
  appBar: {
    zIndex: theme.zIndex.appBar,
    position: 'fixed',
    width: 'auto',
    left: theme.shape.header.left,
    right: theme.shape.header.right,
    top: theme.shape.header.top,
    background: theme.palette.background.header,
    transition: theme.transitions.create('left', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  },
  appBarWide: {
    left: theme.shape.header.leftCollapsed,
  },
  iconBar: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.header,

    '& .MuiIconButton-root svg': {
      fontSize: 25,
    },
  },
}))

export const mainAppBarId = 'main-app-bar'

const pageActionIcons = {
  user: <J2Userprofile />,
  back: <J1LeftarrowC />,
  edit: <J2Editacontent />,
  add: <J2Add />,
  service: <K1Blindness />,
  custom: <J2Add />,
}

export interface HeaderBarProps {
  title?: string
  handleMenuClick: () => void
  handleLogoutClick: () => void
  iconBarContent?: React.ReactNode
  withLanguageSelect?: boolean
  withIcons?: boolean
  /** Amount of padding (in MUI units) on the left of the logout button to separate it from the other controls. */
  logoutGap?: number
  withLanguageEditorMenu?: boolean
  headerClassName?: string
  headerBarClassName?: string
  alwaysShowTitle?: boolean
  collapsedSidebar?: boolean
  elevate?: boolean
  tabs?: TabsBarTab[]
  activeTab?: number
  breadcrumbs?: Breadcrumb[]
  innerRef?: Ref<HTMLDivElement>
  customContent?: React.ReactNode
  showTitle?: boolean
  pageActions?: PageActions
}

const HeaderBar: React.FC<HeaderBarProps> = ({
  title,
  handleMenuClick,
  handleLogoutClick,
  iconBarContent,
  withLanguageSelect,
  withIcons,
  logoutGap,
  withLanguageEditorMenu = true,
  headerClassName,
  headerBarClassName,
  alwaysShowTitle,
  collapsedSidebar,
  elevate,
  tabs,
  activeTab,
  breadcrumbs,
  innerRef,
  customContent,
  showTitle = true,
  pageActions = [],
  ...rest
}) => {
  const hasTabs = !!tabs && tabs.length !== 0
  const { classes, cx } = useStyles({ alwaysShowTitle, withTabs: hasTabs })
  const navigate = useNavigate()
  const hasBreadcrumbs = useMemo(() => breadcrumbs && breadcrumbs.length > 0, [breadcrumbs])

  const leftActions = useMemo(
    () =>
      pageActions
        .filter(action => action.position === 'left')
        .map(page => (
          <IconButton
            key={page.type}
            color="inherit"
            aria-label="left action"
            onClick={() => {
              if (page.onClick) {
                page.onClick()
              } else if (page.type === 'back') navigate(-1)
            }}
            size="large"
          >
            {page.type !== 'custom' ? pageActionIcons[page.type] : page.icon}
          </IconButton>
        )),
    [navigate, pageActions]
  )

  const rightActions = useMemo(
    () =>
      pageActions
        .filter(action => action.position === 'right')
        .map((action, index) => (
          <IconButton
            key={`${index}-${action.type}`}
            color="inherit"
            aria-label="right action"
            onClick={() => {
              if (action.onClick) {
                action.onClick()
              } else if (action.type === 'back') navigate(-1)
            }}
            size="large"
          >
            {action.type !== 'custom' ? pageActionIcons[action.type] : action.icon}
          </IconButton>
        )),
    [navigate, pageActions]
  )

  return (
    <Box {...rest}>
      <AppBar
        ref={innerRef}
        position="sticky"
        elevation={elevate ? 4 : 1}
        className={cx(classes.appBar, { [classes.appBarWide]: collapsedSidebar }, headerClassName)}
        id={mainAppBarId}
      >
        <Toolbar className={classes.toolbar}>
          <Box className={cx(classes.headerBar, headerBarClassName)}>
            <Hidden mdUp>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleMenuClick}
                className={classes.menuButton}
                data-qa={'button-header-menu'}
                size="large"
              >
                <MenuIcon />
              </IconButton>
            </Hidden>
            {customContent}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {leftActions}
              {showTitle && alwaysShowTitle ? (
                <Typography variant="h2">{title}</Typography>
              ) : (
                showTitle && (
                  <Hidden smUp>
                    <Typography variant="h2">{title}</Typography>
                  </Hidden>
                )
              )}
            </Box>

            <Box className={classes.iconBar}>
              {rightActions}
              {iconBarContent}
              {withLanguageEditorMenu && <LanguageEditorMenu />}
              {withLanguageSelect && <LanguageSelect withIcons={withIcons} />}
            </Box>
          </Box>
          {hasBreadcrumbs && (
            <Box>
              <Breadcrumbs breadcrumbs={breadcrumbs!} />
            </Box>
          )}
          {hasTabs && (
            <Box sx={theme => ({ height: 27, width: '100%', mt: hasBreadcrumbs ? theme.spacing(2) : 0 })}>
              <TabsBar tabs={tabs} activeTab={activeTab ?? 0} />
            </Box>
          )}
        </Toolbar>
      </AppBar>
    </Box>
  )
}

export default HeaderBar
